import { parse, stringify } from 'yaml';

export type StageKindEdit = 'agent' | 'human' | 'terminal';
export type ArtifactTypeEdit = 'text' | 'image' | 'file' | 'link' | 'run' | 'prototype';

export interface ExitEdit {
  readonly key: string;
  readonly outcome: string;
  readonly title: string;
  readonly when: string;
  readonly to: string;
  readonly commentRequired: boolean;
  readonly raw: Record<string, unknown>;
}

export interface WorkerEdit {
  readonly replicas: number;
  readonly packageRef: string;
  readonly providerRoute: string;
  /** The AI provider account every replica of this stage runs on (ADR-131). */
  readonly connection: string;
  readonly harness: string;
  readonly model: string;
  readonly workspace: string;
  readonly instructions: string;
  readonly skills: readonly string[];
  readonly toolMode: 'default' | 'custom';
  readonly toolAllow: readonly string[];
  readonly toolDeny: readonly string[];
  readonly environment: string;
  readonly threadPolicy: 'persistent' | 'persistent-per-workspace' | 'ephemeral';
  readonly raw: Record<string, unknown>;
}

export interface StageEntryNotificationEdit {
  readonly key: string;
  readonly id: string;
  readonly backgroundAgent: string;
  readonly prompt: string;
  readonly raw: Record<string, unknown>;
}

export interface StageEdit {
  readonly key: string;
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly kind: StageKindEdit;
  readonly scopeGate: '' | 'implementer' | 'reviewer';
  readonly worker: WorkerEdit | null;
  readonly checklistRef: string;
  readonly checklistReworkTo: string;
  readonly checklistBlockedTo: string;
  readonly notifications: readonly StageEntryNotificationEdit[];
  readonly exits: readonly ExitEdit[];
  readonly raw: Record<string, unknown>;
}

export interface ArtifactRequirementEdit {
  readonly key: string;
  readonly id: string;
  readonly type: ArtifactTypeEdit;
  readonly description: string;
  readonly multiple: boolean;
  readonly raw: Record<string, unknown>;
}

export interface ChecklistItemEdit {
  readonly key: string;
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly artifacts: readonly ArtifactRequirementEdit[];
  readonly raw: Record<string, unknown>;
}

export interface ChecklistEdit {
  readonly key: string;
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly items: readonly ChecklistItemEdit[];
  readonly raw: Record<string, unknown>;
}

export interface StageConfigurationEdit {
  readonly initialStage: string;
  readonly cancelledStage: string;
  readonly stages: readonly StageEdit[];
  readonly checklists: readonly ChecklistEdit[];
  readonly backgroundAgentIds: readonly string[];
  readonly raw: Record<string, unknown>;
  readonly workflowRaw: Record<string, unknown>;
}

let nextKey = 0;
const key = (prefix: string): string => `${prefix}-${String(++nextKey)}`;
const record = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {};
const list = (value: unknown): readonly unknown[] => Array.isArray(value) ? value : [];
const text = (source: Record<string, unknown>, name: string): string => {
  const value = source[name];
  return typeof value === 'string' ? value : '';
};

export function readStageConfigurationEdit(source: string): StageConfigurationEdit {
  const parsed = source.trim() === '' ? {} : parse(source);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error('Stage configuration must be a YAML mapping.');
  const raw = parsed as Record<string, unknown>;
  const workflowRaw = record(raw['workflow']);
  return {
    initialStage: text(workflowRaw, 'initialStage'),
    cancelledStage: text(workflowRaw, 'cancelledStage'),
    stages: list(raw['stages']).map(readStage),
    checklists: list(raw['checklists']).map(readChecklist),
    backgroundAgentIds: list(raw['backgroundAgents']).map((agent) => text(record(agent), 'id')).filter(Boolean),
    raw,
    workflowRaw,
  };
}

function readStage(value: unknown): StageEdit {
  const raw = record(value);
  const kindValue = text(raw, 'kind');
  const kind: StageKindEdit = kindValue === 'human' || kindValue === 'terminal' ? kindValue : 'agent';
  const workerRaw = record(raw['worker']);
  const placement = record(workerRaw['placement']);
  const toolAccess = record(workerRaw['toolAccess']);
  const checklist = record(raw['checklist']);
  const onEntry = record(raw['onEntry']);
  return {
    key: key('stage'), id: text(raw, 'id'), title: text(raw, 'title'), description: text(raw, 'description'), kind,
    scopeGate: raw['scopeGate'] === 'implementer' || raw['scopeGate'] === 'reviewer' ? raw['scopeGate'] : '',
    worker: kind === 'agent' ? {
      replicas: typeof workerRaw['replicas'] === 'number' ? workerRaw['replicas'] : 1,
      packageRef: text(workerRaw, 'package'), providerRoute: text(workerRaw, 'providerRoute'),
      connection: text(workerRaw, 'connection'), harness: text(workerRaw, 'harness'), model: text(workerRaw, 'model'), workspace: text(workerRaw, 'workspace'),
      instructions: text(workerRaw, 'instructions'), skills: list(workerRaw['skills']).filter((item): item is string => typeof item === 'string'),
      toolMode: Object.keys(toolAccess).length === 0 ? 'default' : 'custom',
      toolAllow: list(toolAccess['allow']).filter((item): item is string => typeof item === 'string'),
      toolDeny: list(toolAccess['deny']).filter((item): item is string => typeof item === 'string'),
      environment: text(placement, 'environment'),
      threadPolicy: workerRaw['threadPolicy'] === 'ephemeral' || workerRaw['threadPolicy'] === 'persistent-per-workspace' ? workerRaw['threadPolicy'] : 'persistent',
      raw: workerRaw,
    } : null,
    checklistRef: text(checklist, 'ref'), checklistReworkTo: text(checklist, 'reworkTo'), checklistBlockedTo: text(checklist, 'blockedTo'),
    notifications: list(onEntry['notifications']).map(readNotification),
    exits: kind === 'terminal' ? [] : list(raw['exits']).map(readExit), raw,
  };
}

function readNotification(value: unknown): StageEntryNotificationEdit {
  const raw = record(value);
  return { key: key('notification'), id: text(raw, 'id'), backgroundAgent: text(raw, 'backgroundAgent'), prompt: text(raw, 'prompt'), raw };
}

function readExit(value: unknown): ExitEdit {
  const raw = record(value);
  return { key: key('exit'), outcome: text(raw, 'outcome'), title: text(raw, 'title'), when: text(raw, 'when'), to: text(raw, 'to'), commentRequired: raw['commentRequired'] === true, raw };
}

function readChecklist(value: unknown): ChecklistEdit {
  const raw = record(value);
  return { key: key('checklist'), id: text(raw, 'id'), title: text(raw, 'title'), description: text(raw, 'description'), items: list(raw['items']).map(readItem), raw };
}

function readItem(value: unknown): ChecklistItemEdit {
  const raw = record(value);
  return { key: key('item'), id: text(raw, 'id'), title: text(raw, 'title'), description: text(raw, 'description'), artifacts: list(raw['requiredArtifacts']).map(readArtifact), raw };
}

function readArtifact(value: unknown): ArtifactRequirementEdit {
  const raw = record(value); const type = text(raw, 'type');
  return { key: key('artifact'), id: text(raw, 'id'), type: ['image', 'file', 'link', 'run', 'prototype'].includes(type) ? type as ArtifactTypeEdit : 'text', description: text(raw, 'description'), multiple: raw['multiple'] === true, raw };
}

export function writeStageConfigurationEdit(edit: StageConfigurationEdit): string {
  const output = { ...edit.raw, workflow: { ...edit.workflowRaw, initialStage: edit.initialStage, cancelledStage: edit.cancelledStage }, stages: edit.stages.map(writeStage), checklists: edit.checklists.map(writeChecklist) };
  return stringify(output, { lineWidth: 100 }).trimEnd();
}

function writeStage(stage: StageEdit): Record<string, unknown> {
  const output: Record<string, unknown> = { ...stage.raw, id: stage.id, title: stage.title, kind: stage.kind };
  stage.scopeGate === '' ? delete output['scopeGate'] : output['scopeGate'] = stage.scopeGate;
  stage.description.trim() === '' ? delete output['description'] : output['description'] = stage.description;
  if (stage.notifications.length === 0) delete output['onEntry'];
  else output['onEntry'] = {
    ...record(record(stage.raw['onEntry'])),
    notifications: stage.notifications.map((notification) => ({
      ...notification.raw,
      id: notification.id,
      backgroundAgent: notification.backgroundAgent,
      prompt: notification.prompt,
    })),
  };
  if (stage.kind === 'terminal') { delete output['worker']; delete output['checklist']; delete output['exits']; return output; }
  output['exits'] = stage.exits.map((exit) => {
    const written: Record<string, unknown> = { ...exit.raw, outcome: exit.outcome, to: exit.to };
    exit.title.trim() === '' ? delete written['title'] : written['title'] = exit.title;
    exit.when.trim() === '' ? delete written['when'] : written['when'] = exit.when;
    exit.commentRequired ? written['commentRequired'] = true : delete written['commentRequired'];
    return written;
  });
  if (stage.kind === 'human') { delete output['worker']; delete output['checklist']; return output; }
  const worker = stage.worker ?? blankWorker();
  const placement = record(worker.raw['placement']);
  const writtenWorker: Record<string, unknown> = { ...worker.raw, replicas: worker.replicas, package: worker.packageRef, providerRoute: worker.providerRoute, harness: worker.harness, workspace: worker.workspace, threadPolicy: worker.threadPolicy };
  // Absent means "not chosen": the schema refuses an empty identifier, so an
  // unmigrated stage must keep the key off the document rather than blank.
  worker.connection.trim() === '' ? delete writtenWorker['connection'] : writtenWorker['connection'] = worker.connection.trim();
  worker.model.trim() === '' ? delete writtenWorker['model'] : writtenWorker['model'] = worker.model;
  worker.instructions.trim() === '' ? delete writtenWorker['instructions'] : writtenWorker['instructions'] = worker.instructions;
  worker.skills.length === 0 ? delete writtenWorker['skills'] : writtenWorker['skills'] = worker.skills;
  if (worker.toolMode === 'default') delete writtenWorker['toolAccess'];
  else {
    const toolAccess: Record<string, unknown> = {};
    if (worker.toolAllow.length > 0) toolAccess['allow'] = worker.toolAllow;
    if (worker.toolDeny.length > 0) toolAccess['deny'] = worker.toolDeny;
    writtenWorker['toolAccess'] = toolAccess;
  }
  if (worker.environment.trim() === '') {
    delete placement['environment'];
    Object.keys(placement).length === 0 ? delete writtenWorker['placement'] : writtenWorker['placement'] = placement;
  } else writtenWorker['placement'] = { ...placement, environment: worker.environment };
  output['worker'] = writtenWorker;
  if (stage.checklistRef.trim() === '') delete output['checklist']; else {
    const checklist: Record<string, unknown> = { ref: stage.checklistRef, reworkTo: stage.checklistReworkTo };
    if (stage.checklistBlockedTo.trim() !== '') checklist['blockedTo'] = stage.checklistBlockedTo;
    output['checklist'] = checklist;
  }
  return output;
}

const writeChecklist = (checklist: ChecklistEdit): Record<string, unknown> => ({ ...checklist.raw, id: checklist.id, title: checklist.title, description: checklist.description, items: checklist.items.map((item) => ({ ...item.raw, id: item.id, title: item.title, description: item.description, requiredArtifacts: item.artifacts.map((artifact) => {
  const written = { ...artifact.raw, id: artifact.id, type: artifact.type, description: artifact.description } as Record<string, unknown>;
  artifact.multiple ? written['multiple'] = true : delete written['multiple'];
  return written;
}) })) });

export function blankWorker(example?: WorkerEdit | null): WorkerEdit {
  return example ? { ...example, raw: {} } : { replicas: 1, packageRef: 'builtin://coder@1', providerRoute: 'coding-default', connection: '', harness: 'codex', model: '', workspace: 'code-workspace', instructions: '', skills: [], toolMode: 'default', toolAllow: [], toolDeny: [], environment: '', threadPolicy: 'persistent', raw: {} };
}

export function blankStage(kind: StageKindEdit, example?: WorkerEdit | null): StageEdit {
  return { key: key('stage'), id: '', title: '', description: '', kind, scopeGate: '', worker: kind === 'agent' ? blankWorker(example) : null, checklistRef: '', checklistReworkTo: '', checklistBlockedTo: '', notifications: [], exits: kind === 'terminal' ? [] : [], raw: {} };
}

export const blankExit = (): ExitEdit => ({ key: key('exit'), outcome: '', title: '', when: '', to: '', commentRequired: false, raw: {} });
export const blankChecklist = (): ChecklistEdit => ({ key: key('checklist'), id: '', title: '', description: '', items: [], raw: {} });
export const blankChecklistItem = (): ChecklistItemEdit => ({ key: key('item'), id: '', title: '', description: '', artifacts: [], raw: {} });
export const blankArtifact = (): ArtifactRequirementEdit => ({ key: key('artifact'), id: '', type: 'text', description: '', multiple: false, raw: {} });
export const blankStageEntryNotification = (stageId: string, agentId = ''): StageEntryNotificationEdit => ({ key: key('notification'), id: slugId(`${stageId || 'stage'}-${agentId || 'background'}-notification`), backgroundAgent: agentId, prompt: '', raw: {} });

function slugId(value: string): string { return value.toLowerCase().replace(/[^a-z0-9-]+/gu, '-').replace(/^-+|-+$/gu, ''); }

export function starterStageConfiguration(): StageConfigurationEdit {
  const development = {
    ...blankStage('agent'), id: 'development', title: 'Development', scopeGate: 'implementer' as const,
    exits: [
      { ...blankExit(), outcome: 'ready', title: 'Ready for review', to: 'review' },
      { ...blankExit(), outcome: 'needs-human', title: 'Needs human', to: 'human-review', commentRequired: true },
    ],
  };
  const review = {
    ...blankStage('agent', { ...development.worker!, packageRef: 'builtin://reviewer@1', workspace: 'review-snapshot' }),
    id: 'review', title: 'Review', scopeGate: 'reviewer' as const,
    exits: [
      { ...blankExit(), outcome: 'approved', title: 'Complete task', to: 'completed' },
      { ...blankExit(), outcome: 'rework', title: 'Return for rework', to: development.id, commentRequired: true },
      { ...blankExit(), outcome: 'needs-human', title: 'Needs human', to: 'human-review', commentRequired: true },
    ],
  };
  const humanReview = {
    ...blankStage('human'), id: 'human-review', title: 'Human review',
    exits: [
      { ...blankExit(), outcome: 'approved', title: 'Approve', to: 'completed' },
      { ...blankExit(), outcome: 'return', title: 'Return to previous stage', to: '$return', commentRequired: true },
    ],
  };
  const backlog = {
    ...blankStage('human'), id: 'backlog', title: 'Backlog', description: 'New work waits for explicit human approval.',
    exits: [
      { ...blankExit(), outcome: 'start', title: 'Start analysis', to: development.id, commentRequired: true },
      { ...blankExit(), outcome: 'cancel', title: 'Cancel', to: 'cancelled', commentRequired: true },
    ],
  };
  return {
    initialStage: backlog.id,
    cancelledStage: 'cancelled',
    stages: [backlog, development, review, humanReview, { ...blankStage('terminal'), id: 'completed', title: 'Completed' }, { ...blankStage('terminal'), id: 'cancelled', title: 'Cancelled' }],
    checklists: [],
    backgroundAgentIds: [],
    raw: {},
    workflowRaw: {},
  };
}

export function renameStage(edit: StageConfigurationEdit, stageKey: string, previous: string, next: string): StageConfigurationEdit {
  if (previous.trim() !== '' && next.trim() === '') return edit;
  if (next.trim() !== '' && edit.stages.some((stage) => stage.key !== stageKey && stage.id === next)) return edit;
  const rewrite = previous.trim() !== '';
  return { ...edit, initialStage: rewrite && edit.initialStage === previous ? next : edit.initialStage, cancelledStage: rewrite && edit.cancelledStage === previous ? next : edit.cancelledStage, stages: edit.stages.map((stage) => ({ ...stage, id: stage.key === stageKey ? next : stage.id, checklistReworkTo: rewrite && stage.checklistReworkTo === previous ? next : stage.checklistReworkTo, checklistBlockedTo: rewrite && stage.checklistBlockedTo === previous ? next : stage.checklistBlockedTo, exits: stage.exits.map((exit) => ({ ...exit, to: rewrite && exit.to === previous ? next : exit.to })) })) };
}

export function renameChecklist(edit: StageConfigurationEdit, checklistKey: string, previous: string, next: string): StageConfigurationEdit {
  if (previous.trim() !== '' && next.trim() === '') return edit;
  if (next.trim() !== '' && edit.checklists.some((checklist) => checklist.key !== checklistKey && checklist.id === next)) return edit;
  const rewrite = previous.trim() !== '';
  return { ...edit, checklists: edit.checklists.map((checklist) => checklist.key === checklistKey ? { ...checklist, id: next } : checklist), stages: edit.stages.map((stage) => rewrite && stage.checklistRef === previous ? { ...stage, checklistRef: next } : stage) };
}

export function validateStageConfigurationEdit(edit: StageConfigurationEdit): readonly string[] {
  const issues: string[] = [];
  const stageIds = edit.stages.map((stage) => stage.id);
  const checklistIds = edit.checklists.map((checklist) => checklist.id);
  requiredUnique(stageIds, 'Stage', issues);
  requiredUnique(checklistIds, 'Checklist', issues);
  const stages = new Map(edit.stages.map((stage) => [stage.id, stage]));
  if (stages.get(edit.initialStage)?.kind === 'terminal' || !stages.has(edit.initialStage)) issues.push('Initial stage must select an agent or human stage.');
  if (stages.get(edit.cancelledStage)?.kind !== 'terminal') issues.push('Cancelled stage must select a terminal stage.');
  for (const stage of edit.stages) {
    const label = stage.title || stage.id || 'Untitled stage';
    if (stage.title.trim() === '') issues.push(`${label}: name is required.`);
    requiredUnique(stage.notifications.map((notification) => notification.id), `${label} notification`, issues);
    for (const notification of stage.notifications) {
      if (!edit.backgroundAgentIds.includes(notification.backgroundAgent)) issues.push(`${label}: notification ${notification.id || 'without an ID'} must select a Background Agent.`);
      if (notification.prompt.trim() === '') issues.push(`${label}: notification ${notification.id || 'without an ID'} needs a prompt.`);
    }
    if (stage.kind === 'agent') {
      const worker = stage.worker;
      if (worker === null || !Number.isInteger(worker.replicas) || worker.replicas < 1 || worker.replicas > 32) issues.push(`${label}: workers must be an integer from 1 to 32.`);
      for (const [field, value] of [['package', worker?.packageRef], ['provider route', worker?.providerRoute], ['harness', worker?.harness], ['workspace', worker?.workspace]] as const) if ((value ?? '').trim() === '') issues.push(`${label}: ${field} is required.`);
      if (stage.checklistRef !== '' && !checklistIds.includes(stage.checklistRef)) issues.push(`${label}: selected checklist does not exist.`);
      if (stage.checklistRef !== '' && (stages.get(stage.checklistReworkTo)?.kind === 'terminal' || !stages.has(stage.checklistReworkTo))) issues.push(`${label}: checklist rework destination must be an agent or human stage.`);
      if (stage.checklistRef !== '' && !stage.exits.some((exit) => exit.to === stage.checklistReworkTo)) issues.push(`${label}: add an outcome to the checklist rework destination.`);
      if (stage.checklistBlockedTo !== '' && stages.get(stage.checklistBlockedTo)?.kind !== 'human') issues.push(`${label}: checklist blocked destination must be a human stage.`);
      if (stage.checklistBlockedTo !== '' && !stage.exits.some((exit) => exit.to === stage.checklistBlockedTo)) issues.push(`${label}: add an outcome to the checklist blocked destination.`);
    }
    if (stage.kind !== 'terminal') {
      requiredUnique(stage.exits.map((exit) => exit.outcome), `${label} outcome`, issues);
      if (stage.exits.length === 0) issues.push(`${label}: add at least one outcome.`);
      for (const exit of stage.exits) {
        if (exit.to === '$return' && stage.kind !== 'human') issues.push(`${label}: only human stages may return to the previous stage.`);
        else if (exit.to !== '$return' && !stages.has(exit.to)) issues.push(`${label}: outcome ${exit.outcome || 'without an ID'} needs a destination.`);
      }
    }
  }
  for (const checklist of edit.checklists) {
    const label = checklist.title || checklist.id || 'Untitled checklist';
    if (checklist.title.trim() === '') issues.push(`${label}: name is required.`);
    requiredUnique(checklist.items.map((item) => item.id), `${label} check`, issues);
    if (checklist.items.length === 0) issues.push(`${label}: add at least one check.`);
    for (const item of checklist.items) {
      const itemLabel = item.title || item.id || 'Untitled check';
      if (item.title.trim() === '' || item.description.trim() === '') issues.push(`${label} / ${itemLabel}: name and verification instructions are required.`);
      requiredUnique(item.artifacts.map((artifact) => artifact.id), `${label} / ${itemLabel} evidence`, issues);
      for (const artifact of item.artifacts) if (artifact.description.trim() === '') issues.push(`${label} / ${itemLabel}: every evidence requirement needs a description.`);
    }
  }
  return [...new Set(issues)];
}

function requiredUnique(values: readonly string[], label: string, issues: string[]): void {
  if (values.some((value) => value.trim() === '')) issues.push(`${label} ID is required.`);
  const seen = new Set<string>();
  for (const value of values.filter(Boolean)) seen.has(value) ? issues.push(`${label} ID “${value}” is duplicated.`) : seen.add(value);
}
